import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ContactUsService } from '../../services/website/contact-us.service';
import { ContactUsResponse } from '../../models/website/contactus/contactUsResponse';
import { ContactPurposeLabels } from '../../models/enums/contactUs';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ContactUsListRequest } from '../../models/website/contactus/contactUsListRequest';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { HttpResponse } from '@angular/common/http';
import { ToastModule } from 'primeng/toast';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';

@Component({
  selector: 'app-contact-us-requests',
  standalone: true,
  imports: [CommonModule, TableModule, InputTextModule, ButtonModule, FormsModule, ToastModule, AutoCompleteModule],
  providers: [MessageService],
  templateUrl: './contact-us-requests.component.html',
  styleUrl: './contact-us-requests.component.scss'
})
export class ContactUsRequestsComponent implements OnInit {
  requests: ContactUsResponse[] = [];
  total = 0;
  first = 0;
  rowSize = 10;
  isSearching = false;

  filterName = '';
  filterPhone = '';
  filterEmail = '';
  isExportLoading = false;

  // Autocomplete suggestion arrays
  nameSuggestions: string[] = [];
  emailSuggestions: string[] = [];
  phoneSuggestions: string[] = [];

  constructor(
    private contactUsService: ContactUsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.load(1);
  }

  getPurposeLabel(purpose: number): string {
    return ContactPurposeLabels[purpose as keyof typeof ContactPurposeLabels] ?? purpose?.toString() ?? '';
  }

  applyFilters(): void {
    this.first = 0;
    this.load(1);
  }

  clearFilters(): void {
    this.filterName = '';
    this.filterPhone = '';
    this.filterEmail = '';
    this.nameSuggestions = [];
    this.emailSuggestions = [];
    this.phoneSuggestions = [];
    this.first = 0;
    this.load(1);
  }

  // Autocomplete: search names after 3 characters
  searchNames(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim();
    if (query && query.length >= 3) {
      this.contactUsService.getContactUsList({
        PageNumber: 1,
        PageSize: 10,
        Name: query
      }).subscribe({
        next: (response) => {
          const unique = [...new Set(
            (response?.result ?? [])
              .map(r => `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim())
              .filter(n => n.toLowerCase().includes(query.toLowerCase()))
          )];
          this.nameSuggestions = unique;
        },
        error: () => { this.nameSuggestions = []; }
      });
    } else {
      this.nameSuggestions = [];
    }
  }

  // Autocomplete: search emails after 3 characters
  searchEmails(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim();
    if (query && query.length >= 3) {
      this.contactUsService.getContactUsList({
        PageNumber: 1,
        PageSize: 10,
        Email: query
      }).subscribe({
        next: (response) => {
          const unique = [...new Set(
            (response?.result ?? [])
              .map(r => r.email ?? '')
              .filter(e => e.toLowerCase().includes(query.toLowerCase()))
          )];
          this.emailSuggestions = unique;
        },
        error: () => { this.emailSuggestions = []; }
      });
    } else {
      this.emailSuggestions = [];
    }
  }

  // Autocomplete: search phone numbers after 3 characters
  searchPhones(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim();
    if (query && query.length >= 3) {
      this.contactUsService.getContactUsList({
        PageNumber: 1,
        PageSize: 10,
        PhoneNumber: query
      }).subscribe({
        next: (response) => {
          const unique = [...new Set(
            (response?.result ?? [])
              .map(r => r.phoneNumber ?? '')
              .filter(p => p.includes(query))
          )];
          this.phoneSuggestions = unique;
        },
        error: () => { this.phoneSuggestions = []; }
      });
    } else {
      this.phoneSuggestions = [];
    }
  }

  onNameSelect(event: any): void {
    const selected: string = typeof event === 'string' ? event : (event?.value || '');
    this.filterName = selected.split(' ')[0];
    this.applyFilters();
  }

  onEmailSelect(): void {
    this.applyFilters();
  }

  onPhoneSelect(): void {
    this.applyFilters();
  }

  load(pageNumber: number): void {
    this.isSearching = true;
    const request: ContactUsListRequest = {
      PageNumber: pageNumber,
      PageSize: this.rowSize
    };
    if (this.filterName?.trim()) request.Name = this.filterName.trim().split(' ')[0];
    if (this.filterPhone?.trim()) request.PhoneNumber = this.filterPhone.trim();
    if (this.filterEmail?.trim()) request.Email = this.filterEmail.trim();

    this.contactUsService.getContactUsList(request).subscribe({
      next: (response) => {
        this.requests = response?.result ?? [];
        this.total = response?.total ?? 0;
        this.isSearching = false;
      },
      error: () => {
        this.isSearching = false;
      }
    });
  }

  downloadContactUsList(): void {
    this.isExportLoading = true;
    const request: ContactUsListRequest = {
      PageNumber: 1,
      PageSize: 2147483647,
      Name: this.filterName?.trim()?.split(' ')[0] || undefined,
      PhoneNumber: this.filterPhone?.trim() || undefined,
      Email: this.filterEmail?.trim() || undefined
    };

    this.contactUsService.downloadContactUsList(request).subscribe({
      next: (resp: HttpResponse<ArrayBuffer>) => {
        const cd = resp.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd)
          || `Contact_Us_Requests_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;
        const blob = new Blob([resp.body!], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        this.saveBlobAsFile(blob, filename);
        this.isExportLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File downloaded successfully' });
      },
      error: (err) => {
        this.isExportLoading = false;
        console.error('Download failed', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download file' });
      }
    });
  }

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.load(pageNumber);
  }

  private getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches != null && matches.length > 1) {
        return matches[1].replace(/['"]/g, '');
      }
    }
    return null;
  }

  private saveBlobAsFile(blob: Blob | null, filename: string): void {
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }
}
